import { putBuild } from "@/common/character/build";

export interface BuildState {
  id: number;
  abilityPointTotal: number;
  archetype: string;
  defenseValues: DefenseValuesState;
  elements: Array<any>; // Not in database
  elementsKnown: Array<any>; // Not in database
  knownAbilities: Array<any>; // Not in database
  hp: number;
  barrier: number;
  hpMax: number;
  mana: number;
  manaMax: number;
  martialPoints: number;
  movespeed: number;
  movespeedMod: number;
  origin: { skill1: string; skill2: string };
  knownElementalGroups: { names: Array<any> }; // Not in database
  knownDivineGroups: { names: Array<any> }; // Not in database
  knownSpellGroups: Array<any>; //Array of Known Magic Paths; 2D array; for each: [spellGroup][maxLevel][Ability Class] // Not in database
  spentAbilityPoints: number;
  placedRolls: PlacedRollsState;
}

export interface DefenseValuesState {
  armor: number;
  movement: number;
  shield: number;
  bonus: number;
}
export interface PlacedRollsState {
  strength: number;
  agility: number;
  health: number;
  perception: number;
  willpower: number;
  charisma: number;
  intelligence: number;
  power: number;
  acrobatics: number;
  alchemy: number;
  awareness: number;
  crafting: number;
  fitness: number;
  interpersonal: number;
  knowledge: number;
  metamagic: number;
  might: number;
  performance: number;
  subtlety: number;
}

export default {
  namespaced: true,
  state: (): BuildState => ({
    id: 0,
    abilityPointTotal: 10,
    archetype: "",
    defenseValues: { armor: 0, movement: 10, shield: 0, bonus: 0 },
    elements: [], // Not in database
    elementsKnown: [], // Not in database
    knownAbilities: [], // Not in database
    hp: 18,
    barrier: 0,
    hpMax: 18,
    mana: 0,
    manaMax: 0,
    martialPoints: 0,
    movespeed: 30,
    movespeedMod: 0,
    origin: { skill1: "none", skill2: "none" },
    knownElementalGroups: { names: [] }, // Not in database
    knownDivineGroups: { names: [] }, // Not in database
    knownSpellGroups: [], //Array of Known Magic Paths; 2D array; for each: [spellGroup][maxLevel][Ability Class] // Not in database
    spentAbilityPoints: 0,
    placedRolls: {
      strength: 0,
      agility: 0,
      health: 0,
      perception: 0,
      willpower: 0,
      charisma: 0,
      intelligence: 0,
      power: 0,
      acrobatics: 0,
      alchemy: 0,
      awareness: 0,
      crafting: 0,
      fitness: 0,
      interpersonal: 0,
      knowledge: 0,
      metamagic: 0,
      might: 0,
      performance: 0,
      subtlety: 0,
    },
  }),
  getters: {
    id: (state: BuildState) => {
      return state.id;
    },
    abilityPointTotal: (state: BuildState) => {
      return state.abilityPointTotal;
    },
    archetype: (state: BuildState) => {
      return state.archetype;
    },
    defenseValues: (state: BuildState) => {
      return state.defenseValues;
    },
    elements: (state: BuildState) => {
      return state.elements;
    },
    elementsKnown: (state: BuildState) => {
      return state.elementsKnown;
    },
    hp: (state: BuildState) => {
      return state.hp;
    },
    hpMax: (state: BuildState) => {
      return state.hpMax;
    },
    mana: (state: BuildState) => {
      return state.mana;
    },
    manaMax: (state: BuildState) => {
      return state.manaMax;
    },
    martialPoints: (state: BuildState) => {
      return state.martialPoints;
    },
    movespeed: (state: BuildState) => {
      return state.movespeed;
    },
    movespeedMod: (state: BuildState) => {
      return state.movespeedMod;
    },
    origin: (state: BuildState) => {
      return state.origin;
    },
    knownSpellGroups: (state: BuildState) => {
      return state.knownSpellGroups;
    },
    checkAbilityPointTotal: (state: BuildState, getters: any) => {
      if (state.abilityPointTotal < getters["usedAbilityPoints"]) {
        return "Spent too many Ability Points";
      }
      return null;
    },
    knownAbilities: (state: BuildState) => {
      return state.knownAbilities;
    },
    spentAbilityPoints: (state: BuildState) => {
      return state.spentAbilityPoints;
    },
    placedRolls: (state: BuildState) => {
      const placedRolls = {
        strength: 0,
        agility: 0,
        health: 0,
        perception: 0,
        willpower: 0,
        charisma: 0,
        intelligence: 0,
        power: 0,
        acrobatics: 0,
        alchemy: 0,
        awareness: 0,
        crafting: 0,
        fitness: 0,
        interpersonal: 0,
        knowledge: 0,
        metamagic: 0,
        might: 0,
        performance: 0,
        subtlety: 0,
      };
      //origin skills
      if (state.origin.skill1 !== "none") {
        const skill1 =
          state.origin.skill1.toLowerCase() as keyof BuildState["placedRolls"];
        placedRolls[skill1] += 1;
      }
      if (
        state.origin.skill2 !== "none" &&
        state.origin.skill1 !== state.origin.skill2
      ) {
        const skill2 =
          state.origin.skill2.toLowerCase() as keyof BuildState["placedRolls"];
        placedRolls[skill2] += 1;
      }
      if (state.archetype === "Augur") {
        placedRolls.acrobatics++;
        placedRolls.alchemy++;
        placedRolls.awareness++;
        placedRolls.crafting++;
        placedRolls.fitness++;
        placedRolls.interpersonal++;
        placedRolls.knowledge++;
        placedRolls.metamagic++;
        placedRolls.might++;
        placedRolls.performance++;
        placedRolls.subtlety++;
      }
      return placedRolls;
    },
    knownElementalGroups: (state: BuildState) => {
      return { ...state.knownElementalGroups };
    },
    build: (state: BuildState) => {
      return {
        id: state.id,
        abilityPointTotal: state.abilityPointTotal,
        archetype: state.archetype,
        defenseValues: state.defenseValues,
        elements: state.elements,
        elementsKnown: state.elementsKnown,
        knownAbilities: state.knownAbilities,
        hp: state.hp,
        hpMax: state.hpMax,
        mana: state.mana,
        manaMax: state.manaMax,
        martialPoints: state.martialPoints,
        movespeed: state.movespeed,
        movespeedMod: state.movespeedMod,
        origin: state.origin,
        knownElementalGroups: state.knownElementalGroups,
        knownDivineGroups: state.knownDivineGroups,
        knownSpellGroups: state.knownSpellGroups,
        spentAbilityPoints: state.spentAbilityPoints,
        placedRolls: state.placedRolls,
      };
    },
  },
  mutations: {
    setId: (state: BuildState, newData: number) => {
      state.id = newData;
    },
    setAbilityPointTotal: (state: BuildState, newData: number) => {
      state.abilityPointTotal = newData;
    },
    setArchetype: (state: BuildState, newData: string) => {
      state.archetype = newData;
    },
    setDefenseValues: (
      state: BuildState,
      newData: BuildState["defenseValues"]
    ) => {
      state.defenseValues = newData;
    },
    setElements: (state: BuildState, newData: Array<any>) => {
      state.elements = newData;
    },
    setElementsKnown: (state: BuildState, newData: Array<any>) => {
      state.elementsKnown = newData;
    },
    setHp: (state: BuildState, newData: number) => {
      state.hp = newData;
    },
    setBarrier: (state: BuildState, newData: number) => {
      state.barrier = newData;
    },
    setHpMax: (state: BuildState, newData: number) => {
      state.hpMax = newData;
    },
    setKnownAbilities: (state: BuildState, newData: Array<any>) => {
      state.knownAbilities = newData;
    },
    setMana: (state: BuildState, newData: number) => {
      state.mana = newData;
    },
    setManaMax: (state: BuildState, newData: number) => {
      state.manaMax = newData;
    },
    setMartialPoints: (state: BuildState, newData: number) => {
      state.martialPoints = newData;
    },
    setMovespeed: (state: BuildState, newData: number) => {
      state.movespeed = newData;
    },
    setMovespeedMod: (state: BuildState, newData: number) => {
      state.movespeedMod = newData;
    },
    setOrigin: (
      state: BuildState,
      newData: { skill1: string; skill2: string }
    ) => {
      state.origin = newData;
    },
    setSkill1: (state: BuildState, newData: string) => {
      state.origin.skill1 = newData;
    },
    setSkill2: (state: BuildState, newData: string) => {
      state.origin.skill2 = newData;
    },
    setSpentAbilityPoints: (state: BuildState, newData: number) => {
      state.spentAbilityPoints = newData;
    },
    placedRolls: (state: BuildState, newData: BuildState["placedRolls"]) => {
      state.placedRolls = newData;
      state.placedRolls.acrobatics = newData.acrobatics;
      state.placedRolls.alchemy = newData.alchemy;
      state.placedRolls.awareness = newData.awareness;
      state.placedRolls.crafting = newData.crafting;
      state.placedRolls.fitness = newData.fitness;
      state.placedRolls.interpersonal = newData.interpersonal;
      state.placedRolls.knowledge = newData.knowledge;
      state.placedRolls.metamagic = newData.metamagic;
      state.placedRolls.might = newData.might;
      state.placedRolls.performance = newData.performance;
      state.placedRolls.subtlety = newData.subtlety;
    },
  },
  actions: {
    putAbilityPointTotal: (
      context: any,
      newData: { ability_point_total: number; character_id: string }
    ) => {
      context.commit("setAbilityPointTotal", newData.ability_point_total);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putArchetype: (
      context: any,
      newData: { archetype: string; character_id: string }
    ) => {
      context.commit("setArchetype", newData.archetype);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putDefenseValues: (
      context: any,
      newData: { dvs: BuildState["defenseValues"]; character_id: string }
    ) => {
      context.commit("setDefenseValues", newData.dvs);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putHp: (context: any, newData: { hp: number; character_id: string }) => {
      context.commit("setHp", newData.hp);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putMaxHp: (context: any, newData: { hp: number; character_id: string }) => {
      context.commit("setMaxHp", newData.hp);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putMana: (
      context: any,
      newData: { mana: number; character_id: string }
    ) => {
      context.commit("setMana", newData.mana);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putManaMax: (
      context: any,
      newData: { mana: number; character_id: string }
    ) => {
      context.commit("setManaMax", newData.mana);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putMovespeed: (
      context: any,
      newData: { movespeed: number; character_id: string }
    ) => {
      context.commit("setMovespeed", newData.movespeed);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putMovespeedMod: (
      context: any,
      newData: { movespeedMod: number; character_id: string }
    ) => {
      context.commit("setMovespeedMod", newData.movespeedMod);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putOrigin: (
      context: any,
      newData: {
        origin: { skill1: string; skill2: string };
        character_id: string;
      }
    ) => {
      context.commit("setOrigin", newData.origin);
      context.dispatch("putToDatabase", newData.character_id);
    },
    putToDatabase: (context: any, character_id: string) => {
      const build = {
        id: context.getters["id"],
        abilityPointsTotal: context.getters["abilityPointTotal"],
        abilityPointsSpent: context.getters["spentAbilityPoints"],
        archetype: context.getters["archetype"],
        defenseValuesArmor: context.getters["defenseValues"].armor,
        defenseValuesMovement: context.getters["defenseValues"].movement,
        defenseValuesShield: context.getters["defenseValues"].shield,
        defenseValuesBonus: context.getters["defenseValues"].bonus,
        hp: context.getters["hp"],
        hpMax: context.getters["hpMax"],
        mana: context.getters["mana"],
        manaMax: context.getters["manaMax"],
        martialPoints: context.getters["martialPoints"],
        movespeed: context.getters["movespeed"],
        movespeedModifier: context.getters["movespeedMod"],
        originSkill1: context.getters["origin"].skill1,
        originSkill2: context.getters["origin"].skill2,
      };
      putBuild(character_id, build).then((response) => {
        context.commit("setId", response.data[0].id);
      });
    },
  },
};
